import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";
import OwnerMonthGrid from "./calendar/OwnerMonthGrid";
import OwnerDaySheet from "./calendar/OwnerDaySheet";
import { OwnerCalendarSettingsDialog, OwnerRoutineDialog } from "./calendar/OwnerCalendarDialogs";
import { OwnerGoogleCalendarDialog, OwnerGoogleEventDialog } from "./calendar/OwnerGoogleCalendarDialogs";
import { OwnerCommitmentDialog, OwnerFinanceDialog } from "./center/OwnerQuickDialogs";
import OwnerRecurringFromTransactionDialog from "./center/OwnerRecurringFromTransactionDialog";
import { compactActionSx } from "./center/ownerCenterUi";

function localYmd(value = new Date()) {
  const date = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return date.toISOString().slice(0, 10);
}

function shiftMonth(month, delta) {
  const [year, monthNumber] = String(month).split("-").map(Number);
  const date = new Date(year, monthNumber - 1 + delta, 1, 12, 0, 0, 0);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month) {
  const [year, monthNumber] = String(month).split("-").map(Number);
  return new Intl.DateTimeFormat("es-AR", { month: "long", year: "numeric" }).format(new Date(year, monthNumber - 1, 1));
}

function shortDateLabel(dateKey) {
  const date = new Date(`${dateKey}T12:00:00`);
  return new Intl.DateTimeFormat("es-AR", { weekday: "short", day: "numeric", month: "short" }).format(date);
}

const hiddenScrollSx = {
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { width: 0, height: 0, display: "none" },
};

export default function OwnerCalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = React.useMemo(() => localYmd(), []);
  const requestedDate = /^\d{4}-\d{2}-\d{2}$/.test(String(searchParams.get("fecha") || "")) ? String(searchParams.get("fecha")) : today;
  const [selectedDate, setSelectedDate] = React.useState(requestedDate);
  const [month, setMonth] = React.useState(requestedDate.slice(0, 7));
  const [calendar, setCalendar] = React.useState(null);
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [dialog, setDialog] = React.useState("");
  const [transaction, setTransaction] = React.useState(null);

  const load = React.useCallback(async () => {
    setError("");
    try {
      const [calendarResponse, accountResponse] = await Promise.all([
        ownerApi.calendar({ month, date: selectedDate }),
        ownerApi.listFinanceAccounts(),
      ]);
      setCalendar(calendarResponse.calendar);
      setAccounts(accountResponse.accounts || []);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar tu calendario Owner."));
    } finally {
      setLoading(false);
    }
  }, [month, selectedDate]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (requestedDate === selectedDate) return;
    setSelectedDate(requestedDate);
    setMonth(requestedDate.slice(0, 7));
  }, [requestedDate, selectedDate]);

  const selectDate = (date) => {
    setSelectedDate(date);
    setSearchParams({ fecha: date }, { replace: true });
    if (date.slice(0, 7) !== month) setMonth(date.slice(0, 7));
  };

  const moveMonth = (delta) => {
    const next = shiftMonth(month, delta);
    setMonth(next);
    setSelectedDate(`${next}-01`);
    setSearchParams({ fecha: `${next}-01` }, { replace: true });
  };

  const goToday = () => {
    setMonth(today.slice(0, 7));
    setSelectedDate(today);
    setSearchParams({ fecha: today }, { replace: true });
  };

  const closeDialog = () => { setDialog(""); setTransaction(null); };
  const initialScheduledAt = `${selectedDate}T${calendar?.settings?.workdayStart || "09:00"}`;

  if (loading && !calendar) return <DashboardLoadingState label="Armando tu agenda Owner…" minHeight={520} />;

  return (
    <Box
      sx={{
        width: "100%",
        height: { xs: "auto", lg: "100%" },
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: { xs: "visible", lg: "hidden" },
        ...hiddenScrollSx,
      }}
    >
      <Box
        sx={{
          flex: "0 0 auto",
          mb: .65,
          px: { xs: .55, sm: .8 },
          py: .4,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2.2,
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={.35} alignItems="center" sx={{ minWidth: 0 }}>
          <Chip
            size="small"
            label={shortDateLabel(selectedDate)}
            sx={{ fontWeight: 950, textTransform: "capitalize", maxWidth: { xs: 104, sm: 150 } }}
          />
          {calendar?.routineTracking?.monthScheduled ? (
            <Chip
              size="small"
              variant="outlined"
              label={`${calendar.routineTracking.monthCompleted}/${calendar.routineTracking.monthScheduled} rutinas`}
              sx={{ display: { xs: "none", md: "inline-flex" }, fontWeight: 850 }}
            />
          ) : null}
          <Chip
            size="small"
            variant="outlined"
            color={calendar?.googleCalendar?.status === "error" ? "error" : calendar?.googleCalendar?.paused ? "warning" : calendar?.googleCalendar?.connected ? "success" : "default"}
            icon={<CalendarMonthRoundedIcon sx={{ fontSize: "15px !important" }} />}
            label={calendar?.googleCalendar?.status === "error"
              ? "Google · revisar"
              : calendar?.googleCalendar?.paused
                ? "Google · pausado"
                : calendar?.googleCalendar?.connected
                  ? (calendar.googleCalendar.calendarSummary || "Google")
                  : "Conectar Google"}
            onClick={() => setDialog("google")}
            sx={{ display: { xs: "none", sm: "inline-flex" }, fontWeight: 850, maxWidth: 175 }}
          />
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" aria-label="Mes anterior" onClick={() => moveMonth(-1)}><ChevronLeftRoundedIcon fontSize="small" /></IconButton>
          <Typography
            noWrap
            sx={{
              minWidth: { xs: 106, sm: 144 },
              textAlign: "center",
              textTransform: "capitalize",
              fontWeight: 950,
              fontSize: { xs: 12, sm: 13 },
            }}
          >
            {monthLabel(month)}
          </Typography>
          <IconButton size="small" aria-label="Mes siguiente" onClick={() => moveMonth(1)}><ChevronRightRoundedIcon fontSize="small" /></IconButton>
          <Button size="small" startIcon={<TodayRoundedIcon />} onClick={goToday} sx={{ ...compactActionSx, minWidth: 0 }}>
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Hoy</Box>
          </Button>
        </Stack>
      </Box>

      {error ? <Alert severity="error" sx={{ flex: "0 0 auto", mb: .65 }}>{error}</Alert> : null}
      {searchParams.get("google") === "connected" ? <Alert severity="success" sx={{ flex: "0 0 auto", mb: .65 }}>Google Calendar quedó conectado. Los eventos del calendario elegido ya forman parte de tu agenda Owner.</Alert> : null}
      {searchParams.get("google") === "cancelled" ? <Alert severity="info" sx={{ flex: "0 0 auto", mb: .65 }}>La conexión con Google fue cancelada; no cambié nada.</Alert> : null}
      {searchParams.get("google") === "error" ? <Alert severity="warning" sx={{ flex: "0 0 auto", mb: .65 }}>Google no pudo completar la conexión. Abrí “Google Calendar” para ver el estado y reconectar.</Alert> : null}

      {calendar ? (
        <Box
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(300px,.72fr) minmax(0,1.28fr)",
              xl: "minmax(350px,.76fr) minmax(0,1.24fr)",
            },
            gridTemplateRows: { xs: "auto auto", lg: "minmax(0,1fr)" },
            gap: .7,
            overflow: { xs: "visible", lg: "hidden" },
          }}
        >
          <Box sx={{ minWidth: 0, minHeight: { lg: 0 }, height: { lg: "100%" }, overflow: "hidden" }}>
            <OwnerMonthGrid month={calendar.month} days={calendar.days || []} selectedDate={calendar.selectedDate} onSelectDate={selectDate} />
          </Box>
          <Box sx={{ minWidth: 0, minHeight: { lg: 0 }, height: { lg: "100%" }, overflow: { xs: "visible", lg: "hidden" }, ...hiddenScrollSx }}>
            <OwnerDaySheet
              calendar={calendar}
              onNewTask={() => setDialog("task")}
              onNewRoutine={() => setDialog("routine")}
              onSettings={() => setDialog("settings")}
              onNewGoogleEvent={() => setDialog("googleEvent")}
              onGoogleSettings={() => setDialog("google")}
              onNewMovement={() => setDialog("finance")}
              onMakeRecurring={(row) => { setTransaction(row); setDialog("recurring"); }}
              onChanged={load}
            />
          </Box>
        </Box>
      ) : null}

      <OwnerCommitmentDialog open={dialog === "task"} onClose={closeDialog} onSaved={load} initialScheduledAt={initialScheduledAt} initiatives={[]} />
      <OwnerRoutineDialog open={dialog === "routine"} onClose={closeDialog} selectedDate={selectedDate} onSaved={load} />
      <OwnerCalendarSettingsDialog open={dialog === "settings"} onClose={closeDialog} settings={calendar?.settings} onSaved={load} />
      <OwnerGoogleCalendarDialog open={dialog === "google"} onClose={closeDialog} currentIntegration={calendar?.googleCalendar} onChanged={load} />
      <OwnerGoogleEventDialog open={dialog === "googleEvent"} onClose={closeDialog} selectedDate={selectedDate} onSaved={load} />
      <OwnerFinanceDialog open={dialog === "finance"} onClose={closeDialog} accounts={accounts} onSaved={load} />
      <OwnerRecurringFromTransactionDialog open={dialog === "recurring"} onClose={closeDialog} transaction={transaction} recurring={calendar?.recurring} onSaved={load} />
    </Box>
  );
}
